/**
 * Centralized currency formatting utility
 *
 * This utility provides consistent currency formatting across the application
 * with support for multiple currencies and proper symbol display
 */

/**
 * Map of currency codes to their symbols
 * Includes symbols that might not be supported by all fonts
 */
const CURRENCY_SYMBOLS = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'ILS': '₪',
  'JPY': '¥',
  'CNY': '¥',
  'KRW': '₩',
  'INR': '₹',
  'RUB': '₽',
  'TRY': '₺',
  'BRL': 'R$',
  'AUD': 'A$',
  'CAD': 'C$',
  'CHF': 'Fr',
  'SEK': 'kr',
  'NOK': 'kr',
  'DKK': 'kr',
  'PLN': 'zł',
  'THB': '฿',
  'MXN': 'Mex$',
  'ZAR': 'R',
  'SGD': 'S$',
  'HKD': 'HK$',
  'NZD': 'NZ$',
  'AED': 'د.إ',
  'SAR': 'ر.س'
};

/**
 * Get the currency symbol for a currency code
 * Falls back to the currency code itself if symbol is not found
 *
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR', 'ILS')
 * @returns {string} Currency symbol or code
 */
export function getCurrencySymbol(currencyCode) {
  if (!currencyCode) return '$'; // Default to USD symbol

  const code = currencyCode.toUpperCase();
  return CURRENCY_SYMBOLS[code] || code;
}

/**
 * Format a currency amount with the appropriate symbol
 *
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR', 'ILS')
 * @param {boolean} [includeDecimals=true] - Whether to include decimal places
 * @returns {string} Formatted currency string (e.g., '₪100.00', '$50.00')
 */
export function formatCurrency(amount, currencyCode = 'USD', includeDecimals = true) {
  if (amount === null || amount === undefined || amount === '') {
    return '—';
  }

  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) {
    console.warn('Invalid amount for currency formatting:', amount);
    return String(amount);
  }

  const symbol = getCurrencySymbol(currencyCode);
  const formattedAmount = includeDecimals
    ? numericAmount.toFixed(2)
    : Math.round(numericAmount).toString();

  // For currencies that typically show symbol after amount (like some European currencies)
  // we'll keep it simple and always show symbol before for consistency
  return `${symbol}${formattedAmount}`;
}

/**
 * Format a currency amount with full details including currency code
 *
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted currency string with code (e.g., '₪100.00 ILS', '$50.00 USD')
 */
export function formatCurrencyWithCode(amount, currencyCode = 'USD') {
  const formatted = formatCurrency(amount, currencyCode);
  const code = currencyCode ? currencyCode.toUpperCase() : 'USD';
  return `${formatted} ${code}`;
}

/**
 * Format a price range with currency
 *
 * @param {number} minAmount - Minimum amount
 * @param {number} maxAmount - Maximum amount
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted price range (e.g., '$10.00 - $20.00')
 */
export function formatPriceRange(minAmount, maxAmount, currencyCode = 'USD') {
  const min = formatCurrency(minAmount, currencyCode);
  const max = formatCurrency(maxAmount, currencyCode);
  return `${min} - ${max}`;
}

/**
 * Export all formatters as a default object for easier importing
 */
export default {
  getCurrencySymbol,
  formatCurrency,
  formatCurrencyWithCode,
  formatPriceRange
};
